import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./style.css";

function Geo({ isStandalone = true }) {
  // Redefine console.log for non-localhost environments
  if (window.location.hostname !== "localhost") {
    console.log = function () {}; // Redefine to an empty function
  }
  const [geoData, setGeoData] = useState({
    Actions: [], // Initialize Actions as an array
  });
  const location = useLocation();
  const lastActionTime = useRef(Date.now());
  const actionQueue = useRef([]);
  const actionTimeout = useRef(null);

  // List of allowed email addresses
  const allowedEmails = ["gdesalterain@gmail.com", "gonza.titan@gmail.com"];

  // State to track whether the user is authorized
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    // Perform auth check only if the component is standalone
    if (isStandalone) {
      const auth = getAuth();
      onAuthStateChanged(auth, (user) => {
        if (user && allowedEmails.includes(user.email)) {
          // User is logged in and is authorized
          setIsAuthorized(true);
        } else {
          // User is not authorized
          setIsAuthorized(false);
          // Redirect to home page or show an error
          //   window.location.href = "/";
        }
      });
    }
  }, [isStandalone]);

  // Firebase logout function
  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        console.log("User signed out.");
        // Redirect or perform additional actions after logout
        // For instance, redirect to home page:
        // window.location.href = "/";
      })
      .catch((error) => {
        console.error("Error signing out:", error);
      });
  };

  // Function to get a description of an element
  const getElementDescription = (element) => {
    if (element.tagName === "IMG") {
      // For images, use the alt text or part of the src
      return (
        element.alt?.substring(0, 10) ||
        element.src?.split("/").pop().substring(0, 10) ||
        "no-alt"
      );
    } else if (element.tagName === "INPUT") {
      // For input elements, use the placeholder or name
      return (
        element.placeholder?.substring(0, 10) ||
        element.name?.substring(0, 10) ||
        ""
      );
    } else {
      // For other elements, use text content or value
      return (
        element.textContent?.substring(0, 10) ||
        element.value?.substring(0, 10) ||
        ""
      );
    }
  };

  const sendDataToSheet = async (data) => {
    // Prevent data push on localhost
    if (window.location.hostname === "localhost") {
      console.log("Skipping data push on localhost");
      return false;
    }

    try {
      await axios.post(
        "https://script.google.com/macros/s/AKfycbxltKZQ-EhKTCGDASHpezUdkwUExxjnzg-2boDex3MsV4EmPRySaKwl6caS0ZRPLFI/exec",
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            origin: window.location.origin,
          },
        }
      );
      console.log("Data sent to Google Sheets successfully");
      return true;
    } catch (error) {
      console.error("Error sending data to Google Sheets", error);
      return false;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const sessionHash =
        sessionStorage.getItem("sessionHash") ||
        `session_${Math.random().toString(36).substring(2, 15)}`;
      sessionStorage.setItem("sessionHash", sessionHash);

      let storedData = JSON.parse(sessionStorage.getItem("geoData"));
      if (!storedData) {
        const response = await axios.get(
          "https://ipgeolocation.abstractapi.com/v1/?api_key=0b3af33240334875b4cbf9e0c01afad0"
        );
        const transformedData = mapAbstractApiToOldModel(response.data);

        storedData = {
          Session: sessionHash,
          IP: transformedData.query,
          Country: transformedData.country,
          Code: transformedData.countryCode,
          City: transformedData.city,
          Region: `${transformedData.regionName} - ${transformedData.region}`,
          ZIP: transformedData.zip,
          ISP: transformedData.isp,
          ORG: transformedData.org,
          Type: transformedData.as,
          Client: getClientInfo(),
          MAP: `https://www.google.com/maps?q=${transformedData.lat},${transformedData.lon}`,
          URL: location.pathname,
          Date: new Date().toLocaleDateString("en-US", {
            timeZone: "America/Argentina/Buenos_Aires",
          }),
          Time: new Date().toLocaleTimeString("en-US", {
            timeZone: "America/Argentina/Buenos_Aires",
          }),
          Actions: [],
          lat: transformedData.lat,
          lon: transformedData.lon,
        };

        sessionStorage.setItem("geoData", JSON.stringify(storedData));
        console.log("Data set in session:", storedData);

        // Trigger the update to Google Sheets here
        await updateSessionData(storedData, location.pathname);
      } else {
        updateSessionData(storedData, location.pathname);
      }
      setGeoData(storedData);
    };

    fetchData();
    return () => clearTimeout(actionTimeout.current);
  }, [location]);

  const updateSessionData = async (data, currentPath) => {
    // Update the URL, Date, and Time
    data.URL = currentPath;
    data.Date = new Date().toLocaleDateString("en-US", {
      timeZone: "America/Argentina/Buenos_Aires",
    });
    data.Time = new Date().toLocaleTimeString("en-US", {
      timeZone: "America/Argentina/Buenos_Aires",
    });

    // Send data to Google Sheets and check if it was sent successfully
    const dataSent = await sendDataToSheet(data);

    if (dataSent) {
      console.log("Data sent to Google Sheets successfully");

      // Clear dynamic fields like Actions
      data.Actions = [];
      console.log("Dynamic data fields cleared");
    } else {
      console.log("Data was not sent, retaining dynamic fields");
    }

    // Update session storage and state
    sessionStorage.setItem("geoData", JSON.stringify(data));
    setGeoData({ ...data });
    console.log("Session data updated:", data);
  };

  function mapAbstractApiToOldModel(data) {
    return {
      query: data.ip_address,
      country: data.country,
      countryCode: data.country_code,
      region: data.region_iso_code,
      regionName: data.region,
      city: data.city,
      zip: data.postal_code,
      lat: data.latitude,
      lon: data.longitude,
      timezone: data.timezone.name,
      isp: data.connection.isp_name,
      org: data.connection.organization_name,
      as: `AS${data.connection.autonomous_system_number} ${data.connection.autonomous_system_organization}`,
    };
  }

  const lastUserActionTime = useRef(Date.now());

  // Function to check for inactivity and clear actions
  const checkForInactivity = () => {
    const currentTime = Date.now();
    const tenMinutes = 10 * 60 * 1000; // 10 minutes in milliseconds

    if (currentTime - lastUserActionTime.current > tenMinutes) {
      // Clear actions if more than 10 minutes of inactivity
      setGeoData((prevData) => {
        const updatedData = { ...prevData, Actions: [] };
        sessionStorage.setItem("geoData", JSON.stringify(updatedData)); // Also clear actions in session storage
        return updatedData;
      });
      console.log("Actions cleared due to inactivity");
    }
  };


  useEffect(() => {
    // Set up an interval to check for inactivity every minute
    const intervalId = setInterval(checkForInactivity, 60 * 1000); // Check every minute

    return () => clearInterval(intervalId); // Clean up interval
  }, []);

  useEffect(() => {
    const trackUserAction = (event) => {
      const actionDetail = {
        type: event.target.tagName,
        description: getElementDescription(event.target),
        timestamp: new Date().toLocaleString("en-US", {
          timeZone: "America/Argentina/Buenos_Aires",
          hour12: false, // Use 24-hour format
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
        pageURL: location.pathname,
      };

      actionQueue.current.push(actionDetail);

      if (actionTimeout.current) {
        clearTimeout(actionTimeout.current);
      }

      actionTimeout.current = setTimeout(() => {
        processActionQueue();
      }, 5000);

      // Immediate update when navigating away from the page
      if (location.pathname !== window.location.pathname) {
        processActionQueue();
      }
      lastUserActionTime.current = Date.now();
    };

    window.addEventListener("click", trackUserAction);

    return () => {
      window.removeEventListener("click", trackUserAction);
      if (actionTimeout.current) {
        clearTimeout(actionTimeout.current);
      }
    };
  }, [location]);

  const processActionQueue = () => {
    if (
      Date.now() - lastActionTime.current >= 5000 ||
      actionQueue.current.length >= 5
    ) {
      let storedData = JSON.parse(sessionStorage.getItem("geoData"));
      storedData.Actions = storedData.Actions.concat(actionQueue.current);
      updateSessionData(storedData, location.pathname);
      actionQueue.current = [];
      lastActionTime.current = Date.now();
      clearTimeout(actionTimeout.current);
    } else {
      clearTimeout(actionTimeout.current);
      actionTimeout.current = setTimeout(
        processActionQueue,
        5000 - (Date.now() - lastActionTime.current)
      );
    }
  };

  const resetSession = () => {
    sessionStorage.removeItem("geoData");
    sessionStorage.removeItem("sessionHash");
    setGeoData({});
    window.location.reload();
  };

  const getClientInfo = () => {
    const userAgent = navigator.userAgent;
    let browser = userAgent.includes("Chrome")
      ? "Chrome"
      : userAgent.includes("Firefox")
      ? "Firefox"
      : userAgent.includes("Safari")
      ? "Safari"
      : userAgent.includes("Edge")
      ? "Edge"
      : "Unknown Browser";
    let platform = navigator.platform.startsWith("Win")
      ? "Windows"
      : navigator.platform.startsWith("Mac")
      ? "MacOS"
      : navigator.platform.startsWith("Linux")
      ? "Linux"
      : "Unknown Platform";
    return `${browser} on ${platform}`;
  };

  const renderListItem = (key, value) => {
    if (key === "lat" || key === "lon") return null;
    if (key === "MAP") {
      return (
        <li key={key}>
          {key}:{" "}
          <a href={value} target="_blank" rel="noopener noreferrer">
            Open in Google Maps
          </a>
        </li>
      );
    }
    if (key === "Session") {
      return (
        <li key={key}>
          {key}: <strong>{value}</strong>{" "}
          <button onClick={resetSession} className="reset-session-btn">
            Reset Session
          </button>
        </li>
      );
    }
    if (key === "Actions" && Array.isArray(value)) {
      const reversedActions = [...value].reverse(); // Reverse the order of actions
      return (
        <ul key="actions-container">
          <li key="actions-label">Actions:</li>
          {reversedActions.map((action, index) => (
            <li key={`${action.timestamp}-${index}`}>
              {action.type}: {action.description} at {action.timestamp} on{" "}
              {action.pageURL}
            </li>
          ))}
        </ul>
      );
    }

    return (
      <li key={key}>
        {key}: <strong>{value}</strong>
      </li>
    );
  };

  if (!isStandalone) return null; // Render nothing when not standalone
  if (isStandalone && !isAuthorized) return (
    <div className="fb-login">
      <h1>Unauthorized Access</h1>
      <button onClick={handleLogout} className="logout-btn">
        <ion-icon name="log-out-outline"></ion-icon> Logout
      </button>
    </div>
  ); // Show unauthorized message when standalone but not authorized

  return (
    <div className="geo-container">
      <div className="geoblock">
        <div className="geo-header">
          <h2>Your Geo Details</h2>
          <button onClick={handleLogout} className="logout-btn">
            <ion-icon name="log-out-outline"></ion-icon> Logout
          </button>
        </div>

        {geoData && (
          <ul>
            {Object.entries(geoData).map(([key, value]) =>
              renderListItem(key, value)
            )}
          </ul>
        )}
      </div>
      {geoData.lat && geoData.lon && (
        <div className="map">
          <MapContainer
            center={[geoData.lat, geoData.lon]}
            zoom={13}
            scrollWheelZoom={false}
            style={{ height: "400px", width: "100%" }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={[geoData.lat, geoData.lon]}>
              <Popup>
                You are here! <br /> {geoData.City}, {geoData.Country}
              </Popup>
            </Marker>
          </MapContainer>
        </div>
      )}
    </div>
  );
}

export default Geo;
